import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { AlertTriangle, ShieldAlert, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";

const STYLE = {
  critical: { card: "border-red-600 bg-red-50", badge: "bg-red-600 text-white", label: "CRÍTICO" },
  high: { card: "border-orange-500 bg-orange-50", badge: "bg-orange-500 text-white", label: "ALTO" },
  warning: { card: "border-yellow-400 bg-yellow-50", badge: "bg-yellow-400 text-yellow-950", label: "ALERTA" },
};

const fmt = (n) => (n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RiskAlertsBanner() {
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [applying, setApplying] = useState(null);

  const canManageLimits = hasRole("super_admin", "admin", "supervisor");

  const load = async () => {
    try {
      const { data } = await api.get("/alerts/risk");
      setData(data);
    } catch (e) {
      console.warn("risk alerts fetch failed", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  const applySuggested = async (item) => {
    if (!canManageLimits) return;
    setApplying(item.number + item.game_id);
    try {
      await api.post("/number-limits", {
        game_id: item.game_id,
        play_type_id: item.play_type_id,
        number: item.number,
        branch_id: null,
        limit_amount: item.suggested_limit,
      });
      toast.success(`Límite RD$${fmt(item.suggested_limit)} aplicado al #${item.number}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setApplying(null);
  };

  if (!data || !data.enabled || data.flagged_count === 0 || dismissed) return null;

  const top = data.items.slice(0, 3);
  const critical = top.find((i) => i.severity === "critical");
  const wrap = critical ? STYLE.critical : (top[0]?.severity === "high" ? STYLE.high : STYLE.warning);

  return (
    <div className={`border-2 ${wrap.card} rounded-sm p-4`} data-testid="risk-banner">
      <div className="flex items-start gap-3">
        <ShieldAlert className={`w-6 h-6 shrink-0 ${critical ? "text-red-700" : "text-orange-600"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-2">
                Motor de Riesgo
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-black ${wrap.badge}`}>{wrap.label}</span>
              </div>
              <div className="font-black text-lg tracking-tight text-slate-950 mt-0.5" style={{ fontFamily: "Manrope" }}>
                {data.flagged_count} {data.flagged_count === 1 ? "número expuesto" : "números expuestos"} sobre el umbral
              </div>
              <div className="text-xs text-slate-600 font-mono">
                Umbral: RD${fmt(data.threshold_amount)} ({data.threshold_pct}% de RD${fmt(data.daily_sales)} en ventas)
              </div>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="text-slate-500 hover:text-slate-900 self-start"
              data-testid="risk-dismiss"
              title="Ocultar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-3 space-y-2">
            {top.map((it) => (
              <div
                key={`${it.game_id}-${it.play_type_id}-${it.number}`}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 flex items-center gap-3"
                data-testid={`risk-row-${it.number}`}
              >
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center font-mono font-black text-xl ${STYLE[it.severity].badge}`}>
                  {it.number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-950 truncate">
                    {it.game_name} · {it.play_type_name}
                  </div>
                  <div className="text-[11px] text-slate-600 font-mono">
                    Exposición RD${fmt(it.exposure)} × {it.payout_multiplier}x = <span className="font-black text-red-700">RD${fmt(it.potential_payout)}</span>
                    {data.daily_sales > 0 && (
                      <> · {(it.ratio_of_sales * 100).toFixed(0)}% de ventas</>
                    )}
                  </div>
                </div>
                {canManageLimits && data.auto_suggest_limit && (
                  <Button
                    size="sm"
                    onClick={() => applySuggested(it)}
                    disabled={applying === it.number + it.game_id}
                    className="h-8 rounded-sm bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs"
                    data-testid={`risk-apply-${it.number}`}
                    title={`Aplicar límite RD$${fmt(it.suggested_limit)}`}
                  >
                    <Lock className="w-3 h-3 mr-1" /> Limitar a RD${fmt(it.suggested_limit)}
                  </Button>
                )}
              </div>
            ))}
            {data.flagged_count > 3 && (
              <div className="text-xs text-slate-600 px-1">
                + {data.flagged_count - 3} más en /reportes → pestaña Exposición
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
